import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Form,
  Input,
  Button,
  Select,
  message,
  Radio,
  Row,
  Col,
  DatePicker,
  Card,
  Space,
  Divider,
  Checkbox,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import ArticleCreationSchema from "../../../../Schemas/ArticleCreationSchema";
import {
  createArticle,
  updateArticle,
  getMaxArticleCodeByCategory,
} from "../../../../Services/ArticleService";
import { getDepts } from "../../../../Services/DeptService";
import { getColors } from "../../../../Services/ColorService";

// Stable empty object reference to prevent unnecessary re-renders
const EMPTY_DATA = {};

function ArticleCreationForm({
  data,
  categoryData = [],
  brandData = [],
  divisionData = [],
  typeData = [],
  subCategoryData = [],
  callReFetch,
  isEditMode = false,
  onClose,
  callReFetchSubCategory,
  loadingSubCategory,
  principleData = [],
  setSelectedCategory,
  userData = {},
}) {
  // Use stable empty object if data is not provided
  const formData = data || EMPTY_DATA;
  const [loading, setLoading] = useState(false);
  const [deptData, setDeptData] = useState([]);
  const [colorData, setColorData] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);

  // Memoize initial values to prevent unnecessary form resets
  // Only recalculate when formData or isEditMode actually changes
  const initialValues = useMemo(() => {
    // Format prices data for the form
    const formatPricesForForm = () => {
      // If we have prices data, use it
      if (formData?.prices && formData.prices.length > 0) {
        return formData.prices.map((price) => {
          const isApplyToAllColors = price.colorId === null;
          return {
            deptId: price.deptId || "",
            colorId: price.colorId || "",
            harga_internal: price.harga_internal
              ? price.harga_internal.toString()
              : "",
            applyToAllColors: isApplyToAllColors,
          };
        });
      }

      // For backward compatibility with old data structure
      if (isEditMode && formData?.harga_internal !== undefined) {
        // If we're editing an old article with the previous price structure
        return [
          {
            deptId: "",
            colorId: "",
            harga_internal: formData.harga_internal
              ? formData.harga_internal.toString()
              : "",
            applyToAllColors: false,
          },
        ];
      }

      // Default empty price entry
      return [
        {
          deptId: "",
          colorId: "",
          harga_internal: "",
          applyToAllColors: false,
        },
      ];
    };

    return {
      name: formData?.name || "",
      code: formData?.code || "",
      category: formData?.category || "",
      brand: formData?.brand || "",
      subcategory: formData?.subcategory || "",
      division: formData?.division || "",
      type: formData?.type || "",
      categoryCode: formData?.categoryCode || "",
      subcategoryCode: formData?.subcategoryCode || "",
      brandCode: formData?.brandCode || "",
      released_date: formData?.released_date || "",
      hpp: formData?.hpp ? formData.hpp.toString() : "",
      prices: formatPricesForForm(),
      preBarcode: formData?.preBarcode || "",
      principleId: formData?.principleId || null,
      colorList: formData?.colorList || [],
      minSize: formData?.minSize ? formData.minSize.toString() : "",
      maxSize: formData?.maxSize ? formData.maxSize.toString() : "",
    };
    // Only recalculate when formData or isEditMode actually changes
    // formData uses EMPTY_DATA (stable reference) when data is not provided, preventing unnecessary recalculations
  }, [formData, isEditMode]);

  const formik = useFormik({
    initialValues,
    // Only enable reinitialize in edit mode to prevent form resets during creation
    enableReinitialize: isEditMode,
    validationSchema: ArticleCreationSchema,
    validate: (values) => {
      const errors = {};

      // Validate article name based on counter code
      if (values.name && values.name.trim() !== "") {
        const trimmedName = values.name.trim();
        const endsWithO = trimmedName.endsWith(" O");

        if (userData?.counterCode === "ECOMMRI") {
          // ECOMMRI users: name must end with ' O'
          if (!endsWithO) {
            errors.name = "Article name must end with ' O' for ECOMMRI users";
          }
        } else {
          // Non-ECOMMRI users: name cannot end with ' O'
          if (endsWithO) {
            errors.name =
              "Article name cannot end with ' O' for non-ECOMMRI users";
          }
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      // Add debug logging to help identify the issue
      console.log("Form values:", values);

      // Create a more detailed validation that shows which fields are empty
      const emptyFields = [];

      if (isEmpty(values.name)) emptyFields.push("Name");
      if (isEmpty(values.code)) emptyFields.push("Code");
      if (isEmpty(values.brand)) emptyFields.push("Brand");
      if (isEmpty(values.category)) emptyFields.push("Category");
      if (isEmpty(values.subcategory)) emptyFields.push("Subcategory");
      if (isEmpty(values.type)) emptyFields.push("Type");
      if (isEmpty(values.division)) emptyFields.push("Division");

      // Note: subcategoryCode, categoryCode, and brandCode are used for preBarcode calculation
      // but they're not required fields in the Article model

      if (emptyFields.length > 0) {
        console.log("Empty fields:", emptyFields);
        message.error(
          `Please fill in the following fields: ${emptyFields.join(", ")}`
        );
        return;
      }

      // Validate price entries with more detailed error reporting
      const invalidPriceIndices = [];
      values.prices.forEach((price, index) => {
        const missingDept = isEmpty(price.deptId);

        // Only require colorId if applyToAllColors is false
        const needsColor = !price.applyToAllColors && isEmpty(price.colorId);

        // Check if price is missing
        const missingPrice =
          price.harga_internal !== 0 &&
          isEmpty(price.harga_internal?.toString());

        if (missingDept || needsColor || missingPrice) {
          console.log(`Price entry ${index + 1} validation failed:`, {
            missingDept,
            needsColor,
            missingPrice,
            price,
          });
          invalidPriceIndices.push(index + 1);
        }
      });

      if (invalidPriceIndices.length > 0) {
        console.log("Invalid price entries at indices:", invalidPriceIndices);
        message.error(
          `Please complete price entries: ${invalidPriceIndices.join(", ")}`
        );
        return;
      }

      try {
        setLoading(true);
        // No need to add preBarcode separately as it's already in values
        const payload = { ...values };

        // Format prices to ensure proper data types
        payload.prices = payload.prices
          .map((price) => {
            const formattedPrice = {
              ...price,
              harga_internal:
                price.harga_internal === "" ? 0 : Number(price.harga_internal),
            };

            // If apply to all colors is checked, set colorId to null
            if (price.applyToAllColors) {
              formattedPrice.colorId = null;
            } else if (!formattedPrice.colorId) {
              // If colorId is empty but not meant to be null (applyToAllColors is false),
              // we need to ensure it has a value to pass validation
              return null; // This entry will be filtered out below
            }

            // Remove the applyToAllColors field as it's not part of the model
            delete formattedPrice.applyToAllColors;

            return formattedPrice;
          })
          .filter(Boolean); // Remove any null entries

        // Format new fields to ensure proper data types
        payload.minSize = payload.minSize === "" ? 0 : Number(payload.minSize);
        payload.maxSize = payload.maxSize === "" ? 0 : Number(payload.maxSize);
        // colorList is already an array of ObjectIds, no conversion needed

        // Format released_date to ISO string if it's a dayjs object
        if (payload.released_date) {
          payload.released_date = dayjs.isDayjs(payload.released_date)
            ? payload.released_date.toISOString()
            : payload.released_date;
        }

        // Log the formatted prices for debugging
        console.log("Formatted prices for submission:", payload.prices);

        // Log the formatted prices for submission
        console.log("Formatted prices for submission:", payload.prices);

        // Filter out any invalid entries
        payload.prices = payload.prices.filter((price) => {
          // A price entry is valid if it has a department and either:
          // 1. It has a colorId (for specific color pricing), or
          // 2. colorId is null (for all-colors pricing)
          const isValid =
            price.deptId && // Must have a department
            price.colorId !== undefined && // Must have a defined colorId (can be null for all colors)
            price.harga_internal !== undefined; // Must have a price

          if (!isValid) {
            console.log("Filtering out invalid price entry:", price);
          }

          return isValid;
        });

        // Add additional log to verify the final payload before submission
        console.log(
          "Final payload for submission:",
          JSON.stringify(payload.prices, null, 2)
        );

        if (!isEditMode) {
          await createArticle(payload);
          message.success("Article created successfully");
        } else {
          const updatedArticle = await updateArticle(data._id, payload);
          message.success(
            updatedArticle?.data?.message || "Article updated successfully"
          );
        }

        callReFetch();
        onClose();
        formik.resetForm();
      } catch (err) {
        console.error("Error: ", err);
        message.error(err?.response?.data?.message || "Something went wrong.");
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch department data
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        setLoadingDepts(true);
        const response = await getDepts();
        if (response && response.data) {
          setDeptData(response.data);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        message.error("Failed to load departments");
      } finally {
        setLoadingDepts(false);
      }
    };

    fetchDepts();
  }, []);

  // Fetch color data
  useEffect(() => {
    const fetchColors = async () => {
      try {
        setLoadingColors(true);
        const response = await getColors();
        if (response && response.data) {
          setColorData(response.data);
        }
      } catch (error) {
        console.error("Error fetching colors:", error);
        message.error("Failed to load colors");
      } finally {
        setLoadingColors(false);
      }
    };

    fetchColors();
  }, []);

  // Fetch max article code by category when category changes (only for create mode)
  useEffect(() => {
    const fetchMaxCode = async () => {
      if (!isEditMode && formik.values.category) {
        try {
          const response = await getMaxArticleCodeByCategory(
            formik.values.category
          );
          if (response && response.data && response.data.data) {
            const maxCode = response.data.data.maxCodeNumber;
            const nextCode = maxCode + 1;
            const formattedCode = nextCode.toString().padStart(3, "0");
            formik.setFieldValue("code", formattedCode);
            console.log(
              `Auto-generated code for category "${formik.values.category}": ${formattedCode}`
            );
          }
        } catch (error) {
          console.error("Error fetching max article code:", error);
          // If error, start with 001
          formik.setFieldValue("code", "001");
        }
      }
    };

    fetchMaxCode();
  }, [formik.values.category, isEditMode]);

  // Set codes from data when in edit mode
  useEffect(() => {
    if (isEditMode && data) {
      // Find matching brand to get its code
      if (data.brand && brandData.length > 0) {
        const matchingBrand = brandData.find(
          (brand) => brand.name === data.brand
        );
        if (matchingBrand && matchingBrand.code && !formik.values.brandCode) {
          formik.setFieldValue("brandCode", matchingBrand.code);
          console.log("Set brandCode in edit mode:", matchingBrand.code);
        }
      }

      // Find matching category to get its code
      if (data.category && categoryData.length > 0) {
        const matchingCategory = categoryData.find(
          (category) => category.name === data.category
        );
        if (
          matchingCategory &&
          matchingCategory.code &&
          !formik.values.categoryCode
        ) {
          formik.setFieldValue("categoryCode", matchingCategory.code);
          console.log("Set categoryCode in edit mode:", matchingCategory.code);
        }
      }

      // Find matching subcategory to get its code
      if (data.subcategory && subCategoryData.length > 0) {
        const matchingSubCategory = subCategoryData.find(
          (subcategory) => subcategory.name === data.subcategory
        );
        if (
          matchingSubCategory &&
          matchingSubCategory.code &&
          !formik.values.subcategoryCode
        ) {
          formik.setFieldValue("subcategoryCode", matchingSubCategory.code);
          console.log(
            "Set subcategoryCode in edit mode:",
            matchingSubCategory.code
          );
        }
      }
    }
  }, [
    isEditMode,
    data,
    brandData,
    categoryData,
    subCategoryData,
    formik.values.brandCode,
    formik.values.categoryCode,
    formik.values.subcategoryCode,
  ]);

  // Update preBarcode dynamically for both create and edit modes
  useEffect(() => {
    // Always update the PreBarcode when any of its components change
    if (
      formik.values.brandCode &&
      formik.values.categoryCode &&
      formik.values.subcategoryCode &&
      formik.values.code
    ) {
      const calculatedPreBarcode = `${formik.values.brandCode}${formik.values.categoryCode}${formik.values.subcategoryCode}${formik.values.code}`;

      // Only update if the calculated value is different from current value
      if (calculatedPreBarcode !== formik.values.preBarcode) {
        formik.setFieldValue("preBarcode", calculatedPreBarcode);
        console.log(
          "Updated preBarcode:",
          calculatedPreBarcode,
          "Mode:",
          isEditMode ? "Edit" : "Create"
        );
      }
    }
  }, [
    formik.values.brandCode,
    formik.values.categoryCode,
    formik.values.subcategoryCode,
    formik.values.code,
  ]);

  // Calculate preSKU preview
  const calculatePreSKU = () => {
    if (formik.values.brand && formik.values.preBarcode && formik.values.code) {
      // Get first 3 characters from brand name
      const brandNameFirst3 = formik.values.brand.substring(0, 3);
      return `${brandNameFirst3}${formik.values.preBarcode}${formik.values.code}`;
    }
    return "";
  };

  // Helper functions for price entries
  const addPriceEntry = () => {
    const prices = [...formik.values.prices];
    prices.push({
      deptId: "",
      colorId: "",
      harga_internal: "",
      applyToAllColors: false,
    });
    formik.setFieldValue("prices", prices);
  };

  const removePriceEntry = (index) => {
    const prices = [...formik.values.prices];
    if (prices.length > 1) {
      prices.splice(index, 1);
      formik.setFieldValue("prices", prices);
    } else {
      message.warning("At least one price entry is required");
    }
  };

  return (
    <div>
      <h2 className="title" style={{ textAlign: "center" }}>
        {isEditMode ? "Article Detail" : "Create Article"}
      </h2>
      <p style={{ textAlign: "center" }}>Nama & Code Tidak boleh duplicate</p>
      {!isEditMode && (
        <p style={{ textAlign: "center", color: "#666", fontSize: "14px" }}>
          Code akan otomatis diisi berdasarkan kategori yang dipilih
        </p>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <Form
          layout="vertical"
          style={{
            maxWidth: "80vw",
            width: "80vw",
            backgroundColor: "#F5F5F5",
            borderRadius: "8px",
            padding: "16px",
          }}
          onFinish={formik.handleSubmit}
        >
          <Form.Item
            label="Name"
            required
            validateStatus={
              formik.errors.name && formik.touched.name ? "error" : ""
            }
            help={
              formik.errors.name && formik.touched.name
                ? formik.errors.name
                : ""
            }
          >
            <Input
              name="name"
              placeholder="Enter article name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
            />
          </Form.Item>

          <Form.Item label="Brand" required>
            <Select
              showSearch
              placeholder="Select a brand"
              onChange={(value, option) => {
                formik.setFieldValue("brand", option.label);
                formik.setFieldValue("brandCode", option.code);
              }}
              value={formik.values.brand || undefined}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {brandData?.map((brand) => (
                <Select.Option
                  key={brand.name}
                  value={brand.name}
                  label={brand.name}
                  code={brand.code}
                >
                  {brand.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Division" required>
            <Select
              showSearch
              placeholder="Select a division"
              onChange={(value) => {
                formik.setFieldValue("division", value);
              }}
              value={formik.values.division || undefined}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {divisionData?.map((division) => (
                <Select.Option key={division.name} value={division.name}>
                  {division.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Type" required>
            <Select
              showSearch
              placeholder="Select a type"
              onChange={(value) => {
                formik.setFieldValue("type", value);
              }}
              value={formik.values.type || undefined}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {typeData?.map((type) => (
                <Select.Option key={type.name} value={type.name}>
                  {type.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Category" required>
            <Select
              showSearch
              style={{ width: "100%" }}
              placeholder="Select a category"
              onChange={(value, option) => {
                formik.setFieldValue("category", option.label);
                formik.setFieldValue("categoryCode", option.code);
                formik.setFieldValue("subcategory", undefined);
                formik.setFieldValue("subcategoryCode", undefined);
                setSelectedCategory(option.label);
                callReFetchSubCategory();
              }}
              value={formik.values.category || undefined}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {categoryData?.map((category) => (
                <Select.Option
                  key={category.name}
                  value={category.name}
                  label={category.name}
                  code={category.code}
                >
                  {category.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Subcategory" required>
            <Select
              showSearch
              placeholder="Select a subcategory"
              onChange={(value, option) => {
                formik.setFieldValue("subcategory", option.label);
                formik.setFieldValue("subcategoryCode", option.code);
              }}
              value={formik.values.subcategory || undefined}
              loading={loadingSubCategory}
              disabled={!formik.values.category || loadingSubCategory}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subCategoryData?.map((subcategory) => (
                <Select.Option
                  key={subcategory.name}
                  value={subcategory.name}
                  label={subcategory.name}
                  code={subcategory.code}
                >
                  {subcategory.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Principle">
            <Select
              allowClear
              showSearch
              placeholder="Select a principle"
              onChange={(value) =>
                formik.setFieldValue("principleId", value || null)
              }
              value={formik.values.principleId || undefined}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {principleData?.map((principle) => (
                <Select.Option key={principle._id} value={principle._id}>
                  {principle.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Code" required>
            <Input
              name="code"
              placeholder="Enter article code"
              disabled={!isEditMode}
              style={{
                backgroundColor: !isEditMode ? "#f5f5f5" : "white",
                color: !isEditMode ? "#999" : "black",
                cursor: !isEditMode ? "not-allowed" : "text",
              }}
              onChange={(e) => {
                if (isEditMode) {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    let num = Number(value);
                    if (num >= 0 && num <= 999) {
                      let formattedValue = num.toString().padStart(3, "0");
                      formik.setFieldValue("code", formattedValue);
                    }
                  }
                }
              }}
              value={formik.values.code}
            />
          </Form.Item>

          <Form.Item label="PreBarcode">
            <Input value={formik.values.preBarcode} disabled />
          </Form.Item>

          <Form.Item label="PreSKU Preview">
            <Input
              value={calculatePreSKU()}
              disabled
              placeholder="PreSKU will appear here when all fields are filled"
              style={{
                backgroundColor: "#f5f5f5",
                color: "#666",
                fontFamily: "monospace",
                fontSize: "14px",
              }}
            />
          </Form.Item>

          <Form.Item label="HPP (Harga Pokok Pembelian)">
            <Input
              prefix="Rp. "
              type="number"
              placeholder="Enter HPP"
              value={formik.values.hpp}
              onChange={(e) => formik.setFieldValue("hpp", e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>

          <Form.Item label="Color List">
            <Select
              mode="multiple"
              showSearch
              placeholder="Select colors"
              loading={loadingColors}
              value={formik.values.colorList}
              onChange={(value) => formik.setFieldValue("colorList", value)}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {colorData?.map((color) => (
                <Select.Option key={color._id} value={color._id}>
                  {color.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Min Size"
                validateStatus={formik.errors.minSize ? "error" : ""}
                help={formik.errors.minSize}
              >
                <Input
                  type="number"
                  placeholder="Enter minimum size (max 2 digits)"
                  value={formik.values.minSize}
                  max={99}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Restrict to 2 digits maximum
                    if (
                      value === "" ||
                      (Number(value) >= 0 && Number(value) <= 99)
                    ) {
                      formik.setFieldValue("minSize", value);

                      // Clear maxSize if it's less than the new minSize
                      if (
                        formik.values.maxSize &&
                        Number(value) > Number(formik.values.maxSize)
                      ) {
                        formik.setFieldValue("maxSize", "");
                      }

                      // Trigger validation for both fields
                      formik.validateField("minSize");
                      formik.validateField("maxSize");
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Max Size"
                validateStatus={formik.errors.maxSize ? "error" : ""}
                help={formik.errors.maxSize}
              >
                <Input
                  type="number"
                  placeholder="Enter maximum size (max 2 digits)"
                  value={formik.values.maxSize}
                  max={99}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Restrict to 2 digits maximum
                    if (
                      value === "" ||
                      (Number(value) >= 0 && Number(value) <= 99)
                    ) {
                      formik.setFieldValue("maxSize", value);
                      // Trigger validation for both minSize and maxSize
                      formik.validateField("minSize");
                      formik.validateField("maxSize");
                    }
                  }}
                  disabled={
                    !formik.values.minSize || formik.values.minSize === ""
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Price Entries</Divider>

          {formik.values.prices.map((price, index) => (
            <Card
              key={index}
              title={`Price Entry #${index + 1}`}
              style={{ marginBottom: 16 }}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removePriceEntry(index)}
                />
              }
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Department" required>
                    <Select
                      placeholder="Select department"
                      loading={loadingDepts}
                      value={formik.values.prices[index].deptId || undefined}
                      onChange={(value) => {
                        const prices = [...formik.values.prices];
                        prices[index].deptId = value;
                        formik.setFieldValue("prices", prices);
                      }}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {deptData?.map((dept) => (
                        <Select.Option key={dept._id} value={dept._id}>
                          {dept.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Color Options" required={false}>
                    <Checkbox
                      checked={formik.values.prices[index].applyToAllColors}
                      onChange={(e) => {
                        // Create a new copy of the prices array
                        const prices = [...formik.values.prices];

                        // Update the applyToAllColors value
                        prices[index] = {
                          ...prices[index],
                          applyToAllColors: e.target.checked,
                        };

                        // Clear colorId if applying to all colors
                        if (e.target.checked) {
                          prices[index].colorId = "";
                        }

                        // Update the form values
                        formik.setFieldValue("prices", prices);
                      }}
                      style={{ marginBottom: 16 }}
                    >
                      Apply to all colors
                    </Checkbox>

                    {!formik.values.prices[index].applyToAllColors && (
                      <Form.Item
                        label="Color"
                        required
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select color"
                          loading={loadingColors}
                          value={
                            formik.values.prices[index].colorId || undefined
                          }
                          onChange={(value) => {
                            const prices = [...formik.values.prices];
                            prices[index].colorId = value ?? null; // handles clear
                            formik.setFieldValue("prices", prices);
                          }}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              ?.toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {colorData?.map((color) => (
                            <Select.Option key={color._id} value={color._id}>
                              {color.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Price" required>
                    <Input
                      prefix="Rp. "
                      type="number"
                      placeholder="Enter price"
                      value={formik.values.prices[index].harga_internal}
                      onChange={(e) => {
                        // Create a deep copy of the prices array
                        const prices = JSON.parse(
                          JSON.stringify(formik.values.prices)
                        );
                        // Update the price value
                        prices[index].harga_internal = e.target.value;
                        // Set the updated prices array in formik state
                        formik.setFieldValue("prices", prices);
                        // Debug log to verify the update
                        console.log(
                          `Updated price at index ${index} to:`,
                          e.target.value
                        );
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          ))}

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <Button
              type="dashed"
              onClick={addPriceEntry}
              icon={<PlusOutlined />}
            >
              Add Price Entry
            </Button>
          </div>

          <Form.Item label="Released Date" required>
            <DatePicker
              style={{ width: "100%" }}
              onChange={(date) => {
                formik.setFieldValue("released_date", date);
              }}
              value={
                formik.values.released_date
                  ? dayjs.isDayjs(formik.values.released_date)
                    ? formik.values.released_date
                    : dayjs(formik.values.released_date)
                  : null
              }
            />
          </Form.Item>

          <Form.Item style={{ textAlign: "center" }}>
            <Button
              type="primary"
              htmlType="submit"
              className="bg-black"
              loading={loading}
            >
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}

ArticleCreationForm.propTypes = {
  data: PropTypes.object,
  categoryData: PropTypes.array,
  brandData: PropTypes.array,
  divisionData: PropTypes.array,
  typeData: PropTypes.array,
  subCategoryData: PropTypes.array,
  callReFetch: PropTypes.func,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func,
  callReFetchSubCategory: PropTypes.func,
  loadingSubCategory: PropTypes.bool,
  callReFetchDivision: PropTypes.func,
  callReFetchType: PropTypes.func,
  setSelectedCategory: PropTypes.func,
  userData: PropTypes.object,
};

export default ArticleCreationForm;
